import { useEffect, useEffectEvent } from 'react';
import { useTranslation } from 'react-i18next';

import type { CardResult } from '../../../types';
import { Modal } from '../../common/Modal';

/** Presigned URLs for one generated card — recto, and verso when it has one. */
export interface PreviewUrls {
  front: string;
  back?: string;
}

interface PreviewDialogProps {
  /** The card currently shown. Always a member of `viewable`. */
  upn: string;
  /** Every previewable card, in table order — what prev/next walk. */
  viewable: CardResult[];
  urlsByUpn: Record<string, PreviewUrls>;
  onShow: (upn: string) => void;
  onClose: () => void;
}

/**
 * The generated-card preview: the card large, its owner named, and prev/next
 * walking the table's previewable cards. Navigation wraps, like the moderation
 * queue's review dialog, and answers to the same keys: ←/→ to move, Escape to
 * close.
 */
export function PreviewDialog({
  upn,
  viewable,
  urlsByUpn,
  onShow,
  onClose,
}: PreviewDialogProps) {
  const { t } = useTranslation();
  const idx = viewable.findIndex((r) => r.upn === upn);

  const step = (delta: number) => {
    const target = viewable[(idx + delta + viewable.length) % viewable.length];
    if (target) {
      onShow(target.upn);
    }
  };
  // An effect event, so the mount-once listener below always reads the current
  // `step` — deps would force a window re-subscription per render, since
  // `step` closes over the shown card's index. Escape is the <dialog>'s own.
  const handleKey = useEffectEvent((e: KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      step(-1);
    } else if (e.key === 'ArrowRight') {
      step(1);
    }
  });
  // Rendered only while open, so the listener's lifetime is the dialog's.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => handleKey(e);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // The caller only opens a card from `viewable`, whose entries all have URLs.
  const card = viewable[idx];
  const urls = urlsByUpn[upn];
  if (!card || !urls) {
    return null;
  }

  // Escape and a click beside the panel close it (native <dialog>, see Modal).
  return (
    <Modal
      aria-label={t('cardGenerator.results.previewTitle')}
      onClose={onClose}
      className="backdrop:bg-ink/80"
    >
      <div className="flex w-full max-w-6xl flex-col border border-ink bg-card">
        <OwnerHeader card={card} onClose={onClose} />
        <CardImages urls={urls} />
        <BatchPager
          index={idx}
          total={viewable.length}
          onPrev={() => step(-1)}
          onNext={() => step(1)}
        />
      </div>
    </Modal>
  );
}

/** Who the card belongs to, and the way out. */
function OwnerHeader({
  card,
  onClose,
}: {
  card: CardResult;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between gap-4 border-b border-b-rule px-4 py-3">
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold text-ink">
          {card.displayName ?? '—'}
        </p>
        <p className="mono truncate text-xs text-muted">{card.upn}</p>
      </div>
      <button
        type="button"
        aria-label={t('common.close')}
        onClick={onClose}
        className="flex size-7 shrink-0 items-center justify-center border border-rule text-lg leading-none text-ink"
      >
        ×
      </button>
    </div>
  );
}

/** The card itself, as large as the viewport allows — recto alone gets the
 *  whole panel; a verso splits it. */
function CardImages({ urls }: { urls: PreviewUrls }) {
  const { t } = useTranslation();
  const sideStyle = {
    maxWidth: urls.back ? '48%' : '100%',
    maxHeight: '70vh',
    background: 'var(--card)',
    border: '1px solid var(--rule)',
  };
  return (
    <div className="flex items-center justify-center gap-4 bg-ink/4 p-4">
      <img
        src={urls.front}
        alt={t('cardGenerator.results.previewFrontAlt')}
        className="min-w-0 object-contain"
        style={sideStyle}
      />
      {urls.back && (
        <img
          src={urls.back}
          alt={t('cardGenerator.results.previewBackAlt')}
          className="max-w-[48%] min-w-0 object-contain"
          style={{ ...sideStyle }}
        />
      )}
    </div>
  );
}

/** Walk the batch: prev / position / next. */
function BatchPager({
  index,
  total,
  onPrev,
  onNext,
}: {
  index: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between border-t border-t-rule px-4 py-2">
      <button
        type="button"
        onClick={onPrev}
        disabled={total < 2}
        className="btn-link text-[11px] disabled:opacity-40"
      >
        ← {t('cardGenerator.results.prev')}
      </button>
      <span className="mono text-xs text-muted">
        {t('cardGenerator.results.position', { index: index + 1, total })}
      </span>
      <button
        type="button"
        onClick={onNext}
        disabled={total < 2}
        className="btn-link text-[11px] disabled:opacity-40"
      >
        {t('cardGenerator.results.next')} →
      </button>
    </div>
  );
}
